// src/components/GestaoPermissoes.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaSave, FaSyncAlt, FaMagic, FaSearch,
  FaFileContract, FaChartLine, FaFileInvoice, FaUserFriends,
  FaUserSlash, FaChartBar, FaGraduationCap, FaSitemap,

  FaTachometerAlt, FaList, FaCalendarAlt, FaDollarSign, FaCalculator,
  FaFileAlt, FaTasks, FaBookOpen, FaShieldAlt, FaHeadset,
  FaUserShield, FaRoute, FaCommentDots, FaIdBadge, FaParking, FaCog,
  FaFileInvoiceDollar, FaBuilding, FaClipboardList,
  FaCalendarCheck, FaFileUpload, FaFileDownload, FaFileSignature,
  FaReceipt, FaWallet, FaBalanceScale, FaLandmark, FaClipboardCheck,
  FaCreditCard, FaTruck, FaHandHoldingUsd, FaCheckDouble, FaPlug,
  FaGavel, FaShoppingCart, FaUserTie, FaIdCard, FaCar, FaPlusCircle,
  FaStream, FaColumns, FaTicketAlt, FaUsersCog, FaNewspaper, FaTags,
  FaLayerGroup, FaTerminal, FaUserCheck, FaKey,
  FaBullhorn, FaBroadcastTower, FaTools, FaDesktop, FaRegListAlt,
  FaNetworkWired, FaWrench, FaGasPump, FaVial, FaFingerprint, FaCode,
  FaPaperPlane, FaMoneyCheckAlt, FaUserCog, FaRobot, FaExchangeAlt,
  FaSatelliteDish, // Inventário CMDB (Gestão de Frota)

  // --- Novos ícones (espelhamento, diff e comparador) ---
  FaClone, FaArrowRight, FaTimes, FaCheck, FaUndo, FaPlus, FaMinus,
  FaBalanceScaleLeft, FaUsers, FaEquals, FaDownload, FaInfoCircle,
} from 'react-icons/fa';

import './GestaoPermissoes.css';

// =============================================================================
// [PORTAL] Tenant fixo do Portal Corporativo (espelha PORTAL_TENANT_ID do back)
// O grupo "Portal Corporativo" só é exibido quando o usuário selecionado
// pertence a este tenant.
// =============================================================================
const PORTAL_TENANT_ID = 1;

// =============================================================================
// Endpoints
// =============================================================================
const API_USERS_FOR_ACCESS = '/api/usuarios/gestao-acessos/list';
const API_GET_PERMS  = (id) => `/api/permissoes/${id}`;
const API_SAVE_PERMS = (id) => `/api/permissoes/${id}`;

// =============================================================================
// Helpers
// =============================================================================
const safeString = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback;
  return String(v).replace(/[\u0000-\u001F\u007F]/g, '').trim();
};
const safeLower = (v, fallback = '') => safeString(v, fallback).toLowerCase();
const safeInt   = (v, fallback = 0) => { const n = parseInt(v, 10); return Number.isFinite(n) ? n : fallback; };
const isObject  = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

const sanitizeUser = (u) => {
  const obj = isObject(u) ? u : {};
  return {
    id:         safeInt(obj.id, 0),
    nome:       safeString(obj.nome, 'Usuário'),
    email:      safeLower(obj.email, 'sem-email@local'),
    perfil:     safeString(obj.perfil, 'operacional'),
    cargo:      safeString(obj.cargo, ''),
    cliente_id: obj.cliente_id == null ? null : safeInt(obj.cliente_id, 0),
  };
};
const sanitizeUsersArray = (payload) => {
  const arr = Array.isArray(payload) ? payload : (Array.isArray(payload?.items) ? payload.items : []);
  const cleaned = arr.map(sanitizeUser).filter(u => u.id > 0);
  const map = new Map();
  cleaned.forEach(u => map.set(u.id, u));
  return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
};

// =============================================================================
// MÓDULOS DE PERMISSÃO
// =============================================================================

// --- 1. ADMINISTRAÇÃO --------------------------------------------------------
const MODULO_ADMINISTRACAO = [
  { id: 'adm_view',      titulo: 'Aba Administração',   desc: 'Exibe a aba Administração no menu lateral.',  icon: <FaUserShield /> },
  { id: 'adm_usuarios',  titulo: 'Gerenciar Usuários',  desc: 'Criar, editar e inativar contas.',            icon: <FaUsersCog /> },
  { id: 'adm_acessos',   titulo: 'Controle de Acessos', desc: 'Gestão de permissões (esta tela).',           icon: <FaUserCheck /> },
  { id: 'adm_conteudo',  titulo: 'Gestão de Conteúdo',  desc: 'Notícias e avisos do sistema.',               icon: <FaNewspaper /> },
  { id: 'adm_tags',      titulo: 'Gestão de Tags',      desc: 'Etiquetas para clientes/OS.',                 icon: <FaTags /> },
  { id: 'adm_templates', titulo: 'Gestão de Templates', desc: 'Modelos de documentos e e-mails.',            icon: <FaLayerGroup /> },
  { id: 'adm_logs',      titulo: 'Controle de Logs',    desc: 'Auditoria de ações do sistema.',              icon: <FaTerminal /> },
];

// --- 2. FINANCEIRO (sem fin_rentabilidade — movida para Frota) ---------------
const MODULO_FINANCEIRO = [
  { id: 'financeiro_view',        titulo: 'Aba Controle Financeiro',   desc: 'Exibe a aba Financeiro no menu lateral.',   icon: <FaDollarSign /> },
  { id: 'fin_dashboard',          titulo: 'Painel de Controle',        desc: 'Visão geral e KPIs do financeiro.',         icon: <FaTachometerAlt /> },
  { id: 'fin_contas_pagar',       titulo: 'Contas a Pagar',            desc: 'Gestão de compromissos.',                   icon: <FaFileUpload /> },
  { id: 'fin_contas_receber',     titulo: 'Contas a Receber',          desc: 'Recebíveis e faturamento.',                 icon: <FaFileDownload /> },
  { id: 'fin_gerar_boleto',       titulo: 'Gerar Boleto',              desc: 'Emissão e gestão de boletos.',              icon: <FaFileInvoiceDollar /> },
  { id: 'fin_fechamento',         titulo: 'Fechamento Mensal',         desc: 'Cockpit de consolidação.',                  icon: <FaFileSignature /> },
  { id: 'fin_balanco',            titulo: 'Balanço Patrimonial',       desc: 'Ativos, Passivos e PL.',                    icon: <FaBalanceScale /> },
  { id: 'fin_gerenciador_patrimonio', titulo: 'Gerenciador Patrimonial', desc: 'Gestão de bens e ativos.',               icon: <FaLandmark /> },
  { id: 'fin_dre',                titulo: 'D.R.E. Gerencial',          desc: 'Resultado do Exercício.',                   icon: <FaFileInvoiceDollar /> },
  { id: 'fin_balancete',          titulo: 'Balancete de Verificação',  desc: 'Relatório contábil.',                       icon: <FaClipboardList /> },
  { id: 'fin_transacoes',         titulo: 'Transações',                desc: 'Histórico de movimentações.',               icon: <FaExchangeAlt /> },
  { id: 'fin_gastos',             titulo: 'Controle de Gastos',        desc: 'Lançamento de despesas.',                   icon: <FaCreditCard /> },
  { id: 'fin_fluxo_caixa',        titulo: 'Fluxo de Caixa Projetado', desc: 'Projeção futura de saldo.',                 icon: <FaChartLine /> },
  { id: 'fin_pagamentos',         titulo: 'Pagamentos',                desc: 'Baixa em lote.',                            icon: <FaHandHoldingUsd /> },
  { id: 'fin_relatorios',         titulo: 'Relatórios Financeiros',    desc: 'Central de relatórios.',                    icon: <FaRegListAlt /> },
  { id: 'fin_conciliacao',        titulo: 'Conciliação Bancária',      desc: 'Processamento OFX.',                        icon: <FaCheckDouble /> },
  { id: 'fin_acerto_motoristas',  titulo: 'Acerto de Motoristas',      desc: 'Prestação de contas.',                      icon: <FaNetworkWired /> },
  { id: 'fin_rotina_fechamento',  titulo: 'Rotina de Fechamento',      desc: 'Auditoria e fechamento diário.',            icon: <FaFileInvoiceDollar /> },
  { id: 'fin_plano_contas',       titulo: 'Plano de Contas',           desc: 'Estruturação hierárquica e integração.',    icon: <FaRegListAlt /> },
];

// --- 3. BAAS HUB -------------------------------------------------------------
const MODULO_BAAS = [
  { id: 'baas_view',      titulo: 'Aba BaaS Hub',          desc: 'Exibe a aba BaaS Hub no menu lateral.',                    icon: <FaMoneyCheckAlt /> },
  { id: 'baas_dashboard', titulo: 'Dashboard',             desc: 'Visão geral e KPIs da conta Stark Bank.',                  icon: <FaTachometerAlt /> },
  { id: 'baas_cards',     titulo: 'Cartões',               desc: 'Criar, bloquear, configurar limites e MCCs dos cartões.',  icon: <FaCreditCard /> },
  { id: 'baas_wallet',    titulo: 'Conta',                 desc: 'Conta Digital Stark Bank: conectar, saldo, extrato, boleto e transferência.', icon: <FaLandmark /> },
  { id: 'baas_aportes',   titulo: 'Aportes',               desc: 'Receber dinheiro na conta: chave Pix, cobrança Pix/boleto e recebimentos.',  icon: <FaReceipt /> },
  { id: 'baas_monitor',   titulo: 'Monitor',               desc: 'Movimentações da conta em tempo real e histórico.',        icon: <FaStream /> },
  { id: 'baas_conciliacao', titulo: 'Conciliação', desc: 'Concilia prestações de contas com as compras do cartão corporativo.', icon: <FaCheckDouble /> },
];

// --- 4. FISCAL ----------------------------------------------------------------
const MODULO_FISCAL = [
  { id: 'fiscal_view',        titulo: 'Aba Gestão Fiscal',       desc: 'Exibe a aba Fiscal no menu lateral.',        icon: <FaReceipt /> },
  { id: 'fiscal_dashboard',   titulo: 'Visão Geral',             desc: 'Indicadores fiscais e status.',              icon: <FaTachometerAlt /> },
  { id: 'fiscal_notas',       titulo: 'Central de Notas (NFS-e)',desc: 'Listagem e gestão de notas.',                icon: <FaList /> },
  { id: 'fiscal_emitir',      titulo: 'Emissão Avulsa',          desc: 'Emissão manual de notas.',                   icon: <FaPlusCircle /> },
  { id: 'fiscal_parametros',  titulo: 'Parâmetros e Impostos',   desc: 'Configurações fiscais e regras.',            icon: <FaCog /> },
  { id: 'fiscal_certificado', titulo: 'Certificado Digital',     desc: 'Configuração e troca de certificado.',       icon: <FaKey /> },
];

// --- 5. RH -------------------------------------------------------------------
const MODULO_RH = [
  { id: 'rh_completo',          titulo: 'Aba Gestão de RH',        desc: 'Exibe a aba RH no menu lateral.',   icon: <FaIdBadge /> },
  { id: 'rh_base_funcionarios', titulo: 'Base de Funcionários',    desc: 'Cadastro de colaboradores.',        icon: <FaUsersCog /> },
  { id: 'rh_folha_pagamento',   titulo: 'Folha de Pagamento',      desc: 'Cálculo de holerites.',             icon: <FaFileInvoiceDollar /> },
  { id: 'rh_advertencias',      titulo: 'Central de Advertência',  desc: 'Gestão disciplinar.',               icon: <FaFileAlt /> },
  { id: 'rh_rescisoes',         titulo: 'Rescisões e Demissões',   desc: 'Desligamentos.',                    icon: <FaFileAlt /> },
  { id: 'rh_engajamento',       titulo: 'Portal de Engajamento',   desc: 'Clima e cultura.',                  icon: <FaFileAlt /> },
  { id: 'rh_vagas',             titulo: 'Gestão de Vagas',         desc: 'Recrutamento e seleção.',           icon: <FaFileAlt /> },
  { id: 'rh_analytics',         titulo: 'People Analytics',        desc: 'Indicadores de RH.',                icon: <FaChartLine /> },
  { id: 'rh_treinamento',       titulo: 'Treinamento & Dev.',      desc: 'Cursos e capacitação.',             icon: <FaCalendarCheck /> },
  { id: 'rh_desempenho',        titulo: 'Avaliação de Desempenho', desc: 'PDI e feedbacks.',                  icon: <FaClipboardList /> },
  { id: 'rh_beneficios',        titulo: 'Saúde e Benefícios',      desc: 'Convênios e planos.',               icon: <FaClipboardCheck /> },
  { id: 'rh_ponto',             titulo: 'Controle de Ponto',       desc: 'Jornada de trabalho.',              icon: <FaCalendarCheck /> },
  { id: 'rh_organograma',       titulo: 'Organograma',             desc: 'Estrutura hierárquica.',            icon: <FaRegListAlt /> },
];

// --- 6. GESTÃO DE FROTA (inclui fin_rentabilidade + inventario_view + diario_bordo_view) ---
const MODULO_FROTA = [
  { id: 'frota_view',        titulo: 'Aba Gestão de Frota',    desc: 'Exibe a aba Gestão de Frota no menu lateral.',          icon: <FaTruck /> },
  { id: 'operacoes_view',    titulo: 'Central de Operações',   desc: 'Mapa em tempo real e status.',                          icon: <FaRoute /> },
  { id: 'manutencao_view',   titulo: 'Oficinas & Manutenção',  desc: 'Gestão de preventivas e corretivas.',                   icon: <FaWrench /> },
  { id: 'frota_push',        titulo: 'Central de Push',        desc: 'Envio de mensagens para motoristas.',                   icon: <FaPaperPlane /> },
  { id: 'fin_rentabilidade', titulo: 'Rentabilidade da Frota', desc: 'ROI e Custo por KM por veículo.',                       icon: <FaTruck /> },
  { id: 'diario_bordo_view', titulo: 'Diário de Bordo',        desc: 'Inspeções, documentos e prontidão dos motoristas.',     icon: <FaClipboardCheck /> },
  { id: 'inventario_view',   titulo: 'Inventário de Itens',    desc: 'Catálogo de chips e rastreadores (CMDB).',              icon: <FaSatelliteDish /> },
];

// --- 7. CORPORATIVO ----------------------------------------------------------
const MODULO_CORPORATIVO = [
  { id: 'corporativo_view',  titulo: 'Aba Corporativo',           desc: 'Exibe a aba Corporativo no menu lateral.', icon: <FaBuilding /> },
  { id: 'corp_fornecedores', titulo: 'Fornecedores',              desc: 'Cadastro e gestão de parceiros.',          icon: <FaUserTie /> },
  { id: 'corp_cotacoes',     titulo: 'Gestão de Cotações',        desc: 'Análise de orçamentos e compras.',         icon: <FaFileInvoiceDollar /> },
  { id: 'corp_compras',      titulo: 'Compras (PO)',              desc: 'Requisições e ordens de compra.',          icon: <FaShoppingCart /> },
  { id: 'corp_estoque',      titulo: 'Gestão de Estoque',         desc: 'Controle de almoxarifado e insumos.',      icon: <FaGasPump /> },
  { id: 'corp_recebimentos', titulo: 'Recebimento & Conferência', desc: 'Entrada de notas e mercadorias.',          icon: <FaClipboardList /> },
  { id: 'corp_aprovacoes',   titulo: 'Aprovações & Alçadas',      desc: 'Workflow de aprovação financeira.',        icon: <FaClipboardCheck /> },
  { id: 'corp_juridico',     titulo: 'Jurídico & Contratos',      desc: 'Gestão de processos e vigências.',         icon: <FaGavel /> },
  { id: 'corp_qualidade',    titulo: 'Qualidade (SGQ)',           desc: 'Não conformidades e ISO.',                 icon: <FaClipboardCheck /> },
];

// --- 8. CADASTRO -------------------------------------------------------------
const MODULO_CADASTRO = [
  { id: 'cadastro_view',     titulo: 'Aba Cadastro',     desc: 'Exibe a aba Cadastro no menu lateral.', icon: <FaList /> },
  { id: 'cad_clientes',      titulo: 'Clientes',         desc: 'Base de clientes e seguradoras.',       icon: <FaUserTie /> },
  { id: 'cad_motoristas',    titulo: 'Motoristas',       desc: 'Cadastro da equipe de campo.',          icon: <FaIdCard /> },
  { id: 'cad_veiculos',      titulo: 'Veículos',         desc: 'Cadastro da frota.',                    icon: <FaCar /> },
  { id: 'cad_comunicacao',   titulo: 'Comunicação',      desc: 'Histórico e chat.',                     icon: <FaCommentDots /> },
  { id: 'cad_dados_veiculos',titulo: 'Analytics de Frota', desc: 'Painel de abastecimento e Sem Parar por veículo e motorista.', icon: <FaChartLine /> },
  { id: 'cad_cotacoes',      titulo: 'Cotações Ocultas', desc: 'Gestão interna de orçamentos.',         icon: <FaFileContract /> },
];

// --- 9. AGENDA ---------------------------------------------------------------
const MODULO_AGENDA = [
  { id: 'agenda_view', titulo: 'Aba Agenda de Serviços', desc: 'Exibe a aba Agenda no menu lateral.', icon: <FaCalendarAlt /> },
  { id: 'ag_fila',     titulo: 'Fila de Ordens',         desc: 'Visão geral da operação.',            icon: <FaStream /> },
  { id: 'ag_minhas',   titulo: 'Minhas Ordens',          desc: 'OS sob minha responsabilidade.',      icon: <FaClipboardList /> },
  { id: 'ag_cadastro', titulo: 'Cadastro de Ordens',     desc: 'Abrir nova OS (interna).',            icon: <FaPlusCircle /> },
];

// --- 10. FERRAMENTAS (inclui fer_regente) ------------------------------------
const MODULO_FERRAMENTAS_GERAL = [
  { id: 'ferramentas_view', titulo: 'Aba Ferramentas',       desc: 'Exibe a aba Ferramentas no menu lateral.',         icon: <FaTasks /> },
  { id: 'patio_gestao',     titulo: 'Gestão de Pátio',       desc: 'Entrada e saída de veículos.',                     icon: <FaParking /> },
  { id: 'patio_config',     titulo: 'Config. Pátio',         desc: 'Tabelas de preços e regras.',                      icon: <FaCog /> },
  { id: 'fer_tarefas',      titulo: 'Quadro de Tarefas',     desc: 'Kanban interno.',                                  icon: <FaColumns /> },
  { id: 'fer_conhecimento', titulo: 'Base de Conhecimento',  desc: 'Manuais e procedimentos.',                         icon: <FaBookOpen /> },
  { id: 'simulador',        titulo: 'Simulador',             desc: 'Cálculo de prévia e custos.',                      icon: <FaCalculator /> },
  { id: 'relatorios',       titulo: 'Relatórios',            desc: 'Dashboards e exportações.',                        icon: <FaFileAlt /> },
  { id: 'fer_regente',      titulo: 'Regente (IA)',          desc: 'Relatórios inteligentes gerados por IA generativa.', icon: <FaRobot /> },
];

// --- 11. GESTÃO DE RISCOS ----------------------------------------------------
const MODULO_RISCOS = [
  { id: 'riscos_view',        titulo: 'Aba Gestão de Riscos',   desc: 'Exibe a aba Gestão de Riscos no menu lateral.', icon: <FaShieldAlt /> },
  { id: 'riscos_sinistros',   titulo: 'Sinistros & Avarias',    desc: 'Gestão de acidentes e ocorrências.',            icon: <FaGavel /> },
  { id: 'riscos_recuperacao', titulo: 'Recuperação de Crédito', desc: 'Cobrança extrajudicial e acordos.',             icon: <FaHandHoldingUsd /> },
];

// --- 12. SUPORTE -------------------------------------------------------------
const MODULO_SUPORTE = [
  { id: 'suporte_menu_view',     titulo: 'Aba Painel de Suporte',  desc: 'Exibe a aba Suporte no menu lateral.',        icon: <FaHeadset /> },
  { id: 'suporte_view',          titulo: 'Painel de Chamados',     desc: 'Visualizar fila de tickets e suporte.',       icon: <FaTicketAlt /> },
  { id: 'suporte_relatorios',    titulo: 'Relatórios',             desc: 'Relatórios e indicadores do suporte.',        icon: <FaFileAlt /> },
  { id: 'suporte_gmud',          titulo: 'Gestão de Mudanças',     desc: 'Controle de GMUD e mudanças.',                icon: <FaExchangeAlt /> },
  { id: 'suporte_configuracoes', titulo: 'Configurações',          desc: 'Parâmetros e regras do suporte.',             icon: <FaCog /> },
  { id: 'suporte_satisfacao',    titulo: 'Pesq. de Satisfação',    desc: 'Pesquisa e feedback do suporte.',             icon: <FaClipboardCheck /> },
];

// --- 13. INTEGRAÇÕES ---------------------------------------------------------
const MODULO_INTEGRACOES = [
  { id: 'integracoes_view',        titulo: 'Aba Integrações',           desc: 'Exibe a aba Integrações no menu lateral.', icon: <FaPlug /> },
  { id: 'integracoes_seguradoras', titulo: 'Seguradoras (Entrada)',     desc: 'Recebimento de acionamentos.',             icon: <FaShieldAlt /> },
  { id: 'integracoes_portal',      titulo: 'Portal da API',             desc: 'Documentação e credenciais.',              icon: <FaCode /> },
  { id: 'integracoes_api_console', titulo: 'Console de Tráfego & APIs', desc: 'Observabilidade, proxy e governança.',     icon: <FaNetworkWired /> },
];

// --- 14. ANALYTICS -----------------------------------------------------------
const MODULO_ANALYTICS = [
  { id: 'analytics_menu_view',      titulo: 'Aba Analytics',         desc: 'Exibe a aba Analytics no menu lateral.',                  icon: <FaChartLine /> },
  { id: 'analytics_dashboard_view', titulo: 'Análise de Dados',      desc: 'Painel visual de indicadores e auditoria.',               icon: <FaChartLine /> },
  { id: 'analytics_templates_view', titulo: 'Template de Análises',  desc: 'Gestão dos templates semanais de SMS, E-mail e WhatsApp.', icon: <FaPaperPlane /> },
];

// --- 15. PORTAL CORPORATIVO (NOVO — exclusivo do tenant 1) -------------------
// [PORTAL] portal_view: aba do Portal Corporativo (default do tenant 1).
// [PORTAL] portal_config_view: acesso à tela de Configuração do Portal.
//          NUNCA entra em obterPermissoesPadrao → nasce desligada e é liberada
//          manualmente. O backend (routesPortal) exige essa chave de verdade.
const MODULO_PORTAL = [
  { id: 'portal_view',        titulo: 'Portal Corporativo',       desc: 'Acesso ao Portal (notícias, câmbio, assinatura, aniversariantes, etc.). Padrão para o tenant 1.', icon: <FaColumns /> },
  { id: 'portal_config_view', titulo: 'Configuração do Portal',   desc: 'Painel de controle do que aparece no Portal. Liberação manual.', icon: <FaCog /> },
];

// =============================================================================
// Catálogo achatado (id -> título / ícone) p/ diff, espelhamento e comparador
// =============================================================================
const ALL_MODULES = [
  MODULO_ADMINISTRACAO, MODULO_FINANCEIRO, MODULO_BAAS, MODULO_FISCAL,
  MODULO_RH, MODULO_FROTA, MODULO_CORPORATIVO, MODULO_CADASTRO,
  MODULO_AGENDA, MODULO_FERRAMENTAS_GERAL, MODULO_RISCOS, MODULO_SUPORTE,
  MODULO_INTEGRACOES, MODULO_ANALYTICS, MODULO_PORTAL,
];
const META_BY_ID = (() => {
  const map = {};
  ALL_MODULES.forEach(mod => mod.forEach(item => {
    map[item.id] = { titulo: item.titulo, icon: item.icon };
  }));
  return map;
})();
const tituloDe = (id) => META_BY_ID[id]?.titulo || id;
const iconeDe  = (id) => META_BY_ID[id]?.icon || <FaKey />;

// =============================================================================
// Dependências globais (Pai → Filhos)
// =============================================================================
const PERMISSION_DEPENDENCIES = {
  adm_view: [
    'adm_usuarios', 'adm_acessos', 'adm_conteudo', 'adm_tags', 'adm_templates', 'adm_logs',
  ],
  financeiro_view: [
    'fin_dashboard', 'fin_contas_pagar', 'fin_contas_receber', 'fin_gerar_boleto', 'fin_fechamento',
    'fin_balanco', 'fin_gerenciador_patrimonio', 'fin_dre', 'fin_balancete',
    'fin_transacoes', 'fin_gastos', 'fin_fluxo_caixa',
    'fin_pagamentos', 'fin_relatorios', 'fin_conciliacao',
    'fin_acerto_motoristas', 'fin_plano_contas', 'fin_rotina_fechamento',
    // fin_rentabilidade removida — agora pertence a frota_view
  ],
  baas_view: [
    'baas_dashboard', 'baas_cards', 'baas_wallet', 'baas_aportes', 'baas_monitor', 'baas_conciliacao',
  ],
  fiscal_view: [
    'fiscal_dashboard', 'fiscal_notas', 'fiscal_emitir', 'fiscal_parametros', 'fiscal_certificado',
  ],
  rh_completo: [
    'rh_base_funcionarios', 'rh_folha_pagamento', 'rh_advertencias', 'rh_rescisoes',
    'rh_engajamento', 'rh_vagas', 'rh_analytics', 'rh_treinamento', 'rh_desempenho',
    'rh_beneficios', 'rh_ponto', 'rh_organograma',
  ],
  frota_view: [
    'operacoes_view', 'manutencao_view', 'frota_push',
    'fin_rentabilidade',   // movida do financeiro_view
    'diario_bordo_view',   // Diário de Bordo (inspeções, documentos, prontidão)
    'inventario_view',     // Inventário CMDB (chips + rastreadores)
  ],
  corporativo_view: [
    'corp_fornecedores', 'corp_cotacoes', 'corp_compras', 'corp_estoque',
    'corp_aprovacoes', 'corp_recebimentos', 'corp_juridico', 'corp_qualidade',
  ],
  cadastro_view: [
  'cad_clientes', 'cad_comunicacao', 'cad_cotacoes', 'cad_motoristas', 'cad_veiculos', 'cad_dados_veiculos',
],
  agenda_view: [
    'ag_cadastro', 'ag_minhas', 'ag_fila',
  ],
  ferramentas_view: [
    'patio_gestao', 'patio_config', 'fer_tarefas', 'fer_conhecimento',
    'simulador', 'relatorios',
    'fer_regente',  // Regente IA adicionado
  ],
  suporte_menu_view: [
    'suporte_view', 'suporte_relatorios', 'suporte_gmud', 'suporte_configuracoes', 'suporte_satisfacao',
  ],
  integracoes_view: [
    'integracoes_seguradoras', 'integracoes_portal', 'integracoes_api_console',
  ],
  riscos_view: [
    'riscos_sinistros', 'riscos_recuperacao',
  ],
  analytics_menu_view: [
    'analytics_dashboard_view', 'analytics_templates_view',
  ],
  // [PORTAL] configurar o portal implica poder vê-lo; tirar o "ver" tira o "configurar".
  portal_view: [
    'portal_config_view',
  ],
};

// map inverso: filho → pai
const CHILD_TO_PARENT = Object.entries(PERMISSION_DEPENDENCIES).reduce((acc, [parent, children]) => {
  (children || []).forEach((c) => { acc[c] = parent; });
  return acc;
}, {});

// =============================================================================
// Normalização
// =============================================================================
const normalizePermissions = (set) => {
  const s = new Set(set);
  for (const child of Array.from(s)) {
    const parent = CHILD_TO_PARENT[child];
    if (parent) s.add(parent);
  }
  for (const [parent, children] of Object.entries(PERMISSION_DEPENDENCIES)) {
    if (!s.has(parent)) {
      (children || []).forEach((c) => s.delete(c));
    }
  }
  return s;
};

const setsEqual = (a, b) => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

// =============================================================================
// Padrões por Perfil
// =============================================================================
const obterPermissoesPadrao = (perfil) => {
  const novoSet = new Set();
  const addGroup  = (grupo) => { if (grupo) grupo.forEach(item => novoSet.add(item.id)); };
  const removeId  = (id)    => novoSet.delete(id);

  novoSet.add('fer_conhecimento');
  novoSet.add('fer_tarefas');

  // [PORTAL] portal_view é default (a trava real é por tenant, no Sidebar/back).
  // portal_config_view NUNCA é adicionada aqui — liberação é sempre manual.
  novoSet.add('portal_view');

  if (perfil === 'admin' || perfil === 'admin_geral') {
    [
      MODULO_ADMINISTRACAO, MODULO_FINANCEIRO, MODULO_BAAS, MODULO_FISCAL,
      MODULO_FROTA, MODULO_CORPORATIVO, MODULO_RH, MODULO_CADASTRO,
      MODULO_AGENDA, MODULO_FERRAMENTAS_GERAL, MODULO_SUPORTE,
      MODULO_RISCOS, MODULO_INTEGRACOES, MODULO_ANALYTICS,
    ].forEach(addGroup);
    // admin / admin_geral recebem o Diário de Bordo por padrão (vem em MODULO_FROTA).
    // Obs.: NÃO adicionamos MODULO_PORTAL aqui — portal_config_view deve nascer OFF.

  } else {
    novoSet.add('suporte_view');

    if (perfil === 'financeiro') {
      [
        MODULO_FINANCEIRO, MODULO_BAAS, MODULO_FROTA, MODULO_CORPORATIVO,
        MODULO_CADASTRO, MODULO_FERRAMENTAS_GERAL, MODULO_RISCOS,
        MODULO_FISCAL, MODULO_ANALYTICS,
      ].forEach(addGroup);
      removeId('fin_rotina_fechamento');
      removeId('fer_regente');       // Regente é opt-in para financeiro
      removeId('inventario_view');   // Inventário é opt-in p/ financeiro
      removeId('diario_bordo_view'); // Diário de Bordo: só Admin/Admin Geral por padrão (opt-in)

    } else if (perfil === 'operacional') {
      [
        MODULO_AGENDA, MODULO_FROTA, MODULO_CORPORATIVO,
        MODULO_CADASTRO, MODULO_FERRAMENTAS_GERAL, MODULO_RISCOS, MODULO_ANALYTICS,
      ].forEach(addGroup);
      MODULO_INTEGRACOES.filter(i => i.id !== 'integracoes_api_console').forEach(i => novoSet.add(i.id));
      removeId('cad_motoristas');
      removeId('fer_regente');       // Regente é opt-in para operacional
      removeId('diario_bordo_view'); // Diário de Bordo: só Admin/Admin Geral por padrão (opt-in)

    } else if (perfil === 'rh') {
      [MODULO_RH, MODULO_FERRAMENTAS_GERAL].forEach(addGroup);
      removeId('fer_regente');
    }
  }

  return normalizePermissions(novoSet);
};

// =============================================================================
// Componente Principal
// =============================================================================
export default function GestaoPermissoes() {
  const [usuarios,            setUsuarios]            = useState([]);
  const [permissoesUsuario,   setPermissoesUsuario]   = useState(new Set());
  const [permissoesOriginais, setPermissoesOriginais] = useState(new Set());
  const [permissoesAlteradas, setPermissoesAlteradas] = useState(false);
  const [filtroPerfil,        setFiltroPerfil]        = useState('todos');
  const [usuarioSelecionado,  setUsuarioSelecionado]  = useState(null);
  const [termoBusca,          setTermoBusca]          = useState('');
  const [loading,             setLoading]             = useState(false);
  const [saving,              setSaving]              = useState(false);

  // --- Filtro de permissões dentro da tela ---
  const [filtroPermissao,     setFiltroPermissao]     = useState('');

  // --- Espelhamento (De → Para) ---
  const [mirrorOpen,          setMirrorOpen]          = useState(false);
  const [mirrorSource,        setMirrorSource]        = useState(null);   // usuário origem
  const [mirrorPerms,         setMirrorPerms]         = useState(null);   // Set normalizado da origem
  const [mirrorBusca,         setMirrorBusca]         = useState('');
  const [mirrorLoading,       setMirrorLoading]       = useState(false);

  // --- Comparador de acessos ---
  const [compareOpen,         setCompareOpen]         = useState(false);
  const [compareUser,         setCompareUser]         = useState(null);
  const [comparePerms,        setComparePerms]        = useState(null);   // Set normalizado do comparado
  const [compareBusca,        setCompareBusca]        = useState('');
  const [compareLoading,      setCompareLoading]      = useState(false);
  const [compareSomenteDif,   setCompareSomenteDif]   = useState(true);

  useEffect(() => { carregarUsuarios(); }, []); // eslint-disable-line

  useEffect(() => {
    if (usuarioSelecionado) {
      buscarPermissoes(usuarioSelecionado.id);
      setPermissoesAlteradas(false);
      // limpa estados auxiliares ao trocar de usuário
      setMirrorSource(null); setMirrorPerms(null);
      setCompareUser(null); setComparePerms(null);
      setFiltroPermissao('');
    } else {
      setPermissoesUsuario(new Set());
      setPermissoesOriginais(new Set());
      setPermissoesAlteradas(false);
    }
  }, [usuarioSelecionado?.id]); // eslint-disable-line

  useEffect(() => {
    setPermissoesAlteradas(!setsEqual(permissoesUsuario, permissoesOriginais));
  }, [permissoesUsuario, permissoesOriginais]);

  async function carregarUsuarios() {
    setLoading(true);
    try {
      const response = await api.get(API_USERS_FOR_ACCESS);
      setUsuarios(sanitizeUsersArray(response.data));
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar lista de usuários.');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  }

  // Busca pura (não mexe no estado principal) — usada por espelhamento/comparador
  async function fetchPermsDe(userId) {
    const response = await api.get(API_GET_PERMS(userId));
    const arr = Array.isArray(response.data) ? response.data : [];
    return normalizePermissions(new Set(arr));
  }

  async function buscarPermissoes(userId) {
    try {
      const response = await api.get(API_GET_PERMS(userId));
      const arr = Array.isArray(response.data) ? response.data : [];
      let normalized = normalizePermissions(new Set(arr));
      if (normalized.size === 0 && usuarioSelecionado) {
        normalized = obterPermissoesPadrao(usuarioSelecionado.perfil);
        toast.info('Este usuário estava sem permissões. O padrão foi pré-carregado. Clique em Salvar!');
        setPermissoesUsuario(normalized);
        setPermissoesOriginais(new Set()); // baseline vazio → "Salvar" fica ativo
      } else {
        setPermissoesUsuario(normalized);
        setPermissoesOriginais(new Set(normalized)); // baseline normalizado → diff preciso
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao buscar permissões atuais.');
      setPermissoesUsuario(new Set());
      setPermissoesOriginais(new Set());
    }
  }

  async function salvarPermissoes() {
    if (!usuarioSelecionado) return;
    setSaving(true);
    try {
      const normalized = normalizePermissions(permissoesUsuario);
      await api.put(API_SAVE_PERMS(usuarioSelecionado.id), { permissoes: Array.from(normalized) });
      toast.success(`Acessos de ${safeString(usuarioSelecionado.nome, 'usuário').split(' ')[0]} atualizados!`);
      setPermissoesUsuario(new Set(normalized));
      setPermissoesOriginais(new Set(normalized));
    } catch (error) {
      console.error(error);
      toast.error('Falha ao salvar permissões.');
    } finally {
      setSaving(false);
    }
  }

  // Grupos visíveis por perfil
  const gruposVisiveis = useMemo(() => {
    if (!usuarioSelecionado) return [];
    const p = usuarioSelecionado.perfil;
    const grupos = [];

    if (p === 'admin' || p === 'admin_geral') {
      grupos.push({ nome: 'Administração',        itens: MODULO_ADMINISTRACAO,     cor: 'var(--slate-800)' });
      grupos.push({ nome: 'Financeiro',           itens: MODULO_FINANCEIRO,        cor: 'var(--blue-500)' });
      grupos.push({ nome: 'BaaS Hub (Stark Bank)',    itens: MODULO_BAAS,              cor: '#7c3aed' });
      grupos.push({ nome: 'Fiscal',               itens: MODULO_FISCAL,            cor: 'var(--amber-500)' });
      grupos.push({ nome: 'Gestão de Frota',      itens: MODULO_FROTA,             cor: 'var(--indigo-500)' });
      grupos.push({ nome: 'Corporativo',          itens: MODULO_CORPORATIVO,       cor: 'var(--gray-600)' });
      grupos.push({ nome: 'Recursos Humanos',     itens: MODULO_RH,                cor: 'var(--purple-500)' });
      grupos.push({ nome: 'Cadastros',            itens: MODULO_CADASTRO,          cor: 'var(--cyan-500)' });
      grupos.push({ nome: 'Operacional (Agenda)', itens: MODULO_AGENDA,            cor: 'var(--orange-500)' });
      grupos.push({ nome: 'Ferramentas & IA',     itens: MODULO_FERRAMENTAS_GERAL, cor: 'var(--teal-500)' });
      grupos.push({ nome: 'Painel de Suporte',    itens: MODULO_SUPORTE,           cor: 'var(--slate-600)' });
      grupos.push({ nome: 'Gestão de Riscos',     itens: MODULO_RISCOS,            cor: 'var(--red-500)' });
      grupos.push({ nome: 'Integrações',          itens: MODULO_INTEGRACOES,       cor: 'var(--indigo-600)' });
      grupos.push({ nome: 'Analytics',            itens: MODULO_ANALYTICS,         cor: 'var(--blue-600)' });

    } else if (p === 'financeiro') {
      grupos.push({ nome: 'Módulo Financeiro',    itens: MODULO_FINANCEIRO,        cor: 'var(--blue-500)' });
      grupos.push({ nome: 'BaaS Hub (Stark Bank)',    itens: MODULO_BAAS,              cor: '#7c3aed' });
      grupos.push({ nome: 'Gestão de Frota',      itens: MODULO_FROTA,             cor: 'var(--indigo-500)' });
      grupos.push({ nome: 'Corporativo',          itens: MODULO_CORPORATIVO,       cor: 'var(--gray-600)' });
      grupos.push({ nome: 'Cadastros',            itens: MODULO_CADASTRO,          cor: 'var(--cyan-500)' });
      grupos.push({ nome: 'Ferramentas & IA',     itens: MODULO_FERRAMENTAS_GERAL, cor: 'var(--teal-500)' });
      grupos.push({ nome: 'Gestão de Riscos',     itens: MODULO_RISCOS,            cor: 'var(--red-500)' });
      grupos.push({ nome: 'Fiscal',               itens: MODULO_FISCAL,            cor: 'var(--amber-500)' });
      grupos.push({ nome: 'Painel de Suporte',    itens: MODULO_SUPORTE,           cor: 'var(--slate-600)' });
      grupos.push({ nome: 'Analytics',            itens: MODULO_ANALYTICS,         cor: 'var(--blue-600)' });

    } else if (p === 'operacional') {
      grupos.push({ nome: 'Agenda de Serviços',   itens: MODULO_AGENDA,            cor: 'var(--orange-500)' });
      grupos.push({ nome: 'Gestão de Frota',      itens: MODULO_FROTA,             cor: 'var(--indigo-500)' });
      grupos.push({ nome: 'BaaS Hub (Stark Bank)',    itens: MODULO_BAAS,              cor: '#7c3aed' });
      grupos.push({ nome: 'Corporativo',          itens: MODULO_CORPORATIVO,       cor: 'var(--gray-600)' });
      grupos.push({ nome: 'Cadastros',            itens: MODULO_CADASTRO,          cor: 'var(--cyan-500)' });
      grupos.push({ nome: 'Ferramentas & IA',     itens: MODULO_FERRAMENTAS_GERAL, cor: 'var(--teal-500)' });
      grupos.push({ nome: 'Gestão de Riscos',     itens: MODULO_RISCOS,            cor: 'var(--red-500)' });
      grupos.push({
        nome: 'Integrações',
        itens: MODULO_INTEGRACOES.filter(i => i.id !== 'integracoes_api_console'),
        cor: 'var(--indigo-600)',
      });
      grupos.push({ nome: 'Fiscal',               itens: MODULO_FISCAL,            cor: 'var(--amber-500)' });
      grupos.push({ nome: 'Painel de Suporte',    itens: MODULO_SUPORTE,           cor: 'var(--slate-600)' });
      grupos.push({ nome: 'Analytics',            itens: MODULO_ANALYTICS,         cor: 'var(--blue-600)' });

    } else if (p === 'rh') {
      grupos.push({ nome: 'Recursos Humanos',     itens: MODULO_RH,                cor: 'var(--purple-500)' });
      grupos.push({ nome: 'Ferramentas & IA',     itens: MODULO_FERRAMENTAS_GERAL, cor: 'var(--teal-500)' });
      grupos.push({ nome: 'Fiscal',               itens: MODULO_FISCAL,            cor: 'var(--amber-500)' });
      grupos.push({ nome: 'Painel de Suporte',    itens: MODULO_SUPORTE,           cor: 'var(--slate-600)' });
      grupos.push({ nome: 'Analytics',            itens: MODULO_ANALYTICS,         cor: 'var(--blue-600)' });
    }

    // [PORTAL] Grupo exclusivo do tenant 1 — aparece para QUALQUER perfil,
    // desde que o usuário selecionado pertença ao tenant do portal.
    if (usuarioSelecionado.cliente_id === PORTAL_TENANT_ID) {
      grupos.push({ nome: 'Portal Corporativo', itens: MODULO_PORTAL, cor: 'var(--teal-500)' });
    }

    return grupos;
  }, [usuarioSelecionado]);

  // Universo de permissões realmente editáveis para o perfil atual.
  // Tudo que for espelhado/comparado é limitado a este universo, evitando
  // conceder acessos "órfãos" que esta tela não conseguiria exibir/editar.
  const universoEditavel = useMemo(() => {
    const s = new Set();
    gruposVisiveis.forEach(g => g.itens.forEach(i => s.add(i.id)));
    return s;
  }, [gruposVisiveis]);

  // Grupos com filtro de busca textual aplicado
  const gruposFiltrados = useMemo(() => {
    const t = safeLower(filtroPermissao, '');
    if (!t) return gruposVisiveis;
    return gruposVisiveis
      .map(g => ({
        ...g,
        itens: g.itens.filter(i =>
          safeLower(i.titulo, '').includes(t) ||
          safeLower(i.desc, '').includes(t) ||
          safeLower(i.id, '').includes(t)
        ),
      }))
      .filter(g => g.itens.length > 0);
  }, [gruposVisiveis, filtroPermissao]);

  // -------- Diff em tempo real (changeset) --------
  const changeset = useMemo(() => {
    const adicionadas = [];
    const removidas = [];
    permissoesUsuario.forEach(id => { if (!permissoesOriginais.has(id)) adicionadas.push(id); });
    permissoesOriginais.forEach(id => { if (!permissoesUsuario.has(id)) removidas.push(id); });
    adicionadas.sort((a, b) => tituloDe(a).localeCompare(tituloDe(b)));
    removidas.sort((a, b) => tituloDe(a).localeCompare(tituloDe(b)));
    return { adicionadas, removidas };
  }, [permissoesUsuario, permissoesOriginais]);

  const aplicarPadraoPerfil = useCallback(() => {
    if (!usuarioSelecionado || saving) return;
    const padrao = obterPermissoesPadrao(usuarioSelecionado.perfil);
    setPermissoesUsuario(padrao);
    toast.info(`Padrão de ${usuarioSelecionado.perfil.toUpperCase()} aplicado com sucesso! Clique em Salvar.`);
  }, [usuarioSelecionado, saving]);

  const togglePermissao = useCallback((id) => {
    if (saving) return;
    const novoSet   = new Set(permissoesUsuario);
    const isEnabled = novoSet.has(id);
    if (isEnabled) {
      novoSet.delete(id);
      if (PERMISSION_DEPENDENCIES[id]) PERMISSION_DEPENDENCIES[id].forEach(child => novoSet.delete(child));
    } else {
      novoSet.add(id);
      const parent = CHILD_TO_PARENT[id];
      if (parent) novoSet.add(parent);
    }
    setPermissoesUsuario(normalizePermissions(novoSet));
  }, [permissoesUsuario, saving]);

  const marcarTudoGrupo = useCallback((itens, marcar) => {
    if (saving) return;
    const novoSet = new Set(permissoesUsuario);
    const ids = (itens || []).map(i => i.id);
    if (marcar) {
      ids.forEach(id => novoSet.add(id));
    } else {
      ids.forEach(id => {
        novoSet.delete(id);
        if (PERMISSION_DEPENDENCIES[id]) PERMISSION_DEPENDENCIES[id].forEach(child => novoSet.delete(child));
      });
    }
    setPermissoesUsuario(normalizePermissions(novoSet));
  }, [permissoesUsuario, saving]);

  const reverterAlteracoes = useCallback(() => {
    if (saving) return;
    setPermissoesUsuario(new Set(permissoesOriginais));
    toast.info('Alterações descartadas.');
  }, [permissoesOriginais, saving]);

  // ------------------------- ESPELHAMENTO (De → Para) ------------------------
  const abrirEspelhamento = () => {
    if (!usuarioSelecionado) return;
    setMirrorOpen(true);
    setMirrorSource(null);
    setMirrorPerms(null);
    setMirrorBusca('');
  };

  const selecionarOrigemEspelho = async (user) => {
    setMirrorSource(user);
    setMirrorPerms(null);
    setMirrorLoading(true);
    try {
      const perms = await fetchPermsDe(user.id);
      setMirrorPerms(perms);
    } catch (e) {
      console.error(e);
      toast.error('Não foi possível carregar os acessos de origem.');
      setMirrorSource(null);
    } finally {
      setMirrorLoading(false);
    }
  };

  // Prévia do espelhamento (limitado ao universo editável do alvo)
  const previaEspelho = useMemo(() => {
    if (!mirrorPerms) return null;
    const alvo = new Set();
    let foraDeEscopo = 0;
    mirrorPerms.forEach(id => {
      if (universoEditavel.has(id)) alvo.add(id);
      else foraDeEscopo++;
    });
    const final = normalizePermissions(alvo);
    const adicionadas = [];
    const removidas = [];
    final.forEach(id => { if (!permissoesUsuario.has(id)) adicionadas.push(id); });
    permissoesUsuario.forEach(id => { if (!final.has(id)) removidas.push(id); });
    return { final, adicionadas, removidas, foraDeEscopo };
  }, [mirrorPerms, universoEditavel, permissoesUsuario]);

  const confirmarEspelhamento = () => {
    if (!previaEspelho) return;
    if (setsEqual(previaEspelho.final, permissoesUsuario)) {
      toast.info('Os acessos já são idênticos — nada a espelhar.');
      setMirrorOpen(false);
      return;
    }
    setPermissoesUsuario(new Set(previaEspelho.final));
    const nome = safeString(mirrorSource?.nome, 'origem').split(' ')[0];
    let msg = `Acessos espelhados de ${nome}. Revise abaixo e clique em Salvar.`;
    if (previaEspelho.foraDeEscopo > 0) {
      msg += ` (${previaEspelho.foraDeEscopo} fora do escopo deste perfil foram ignorados.)`;
    }
    toast.success(msg);
    setMirrorOpen(false);
  };

  // ------------------------- COMPARADOR DE ACESSOS ---------------------------
  const abrirComparador = () => {
    if (!usuarioSelecionado) return;
    setCompareOpen(true);
    setCompareUser(null);
    setComparePerms(null);
    setCompareBusca('');
    setCompareSomenteDif(true);
  };

  const selecionarComparado = async (user) => {
    setCompareUser(user);
    setComparePerms(null);
    setCompareLoading(true);
    try {
      const perms = await fetchPermsDe(user.id);
      setComparePerms(perms);
    } catch (e) {
      console.error(e);
      toast.error('Não foi possível carregar os acessos para comparação.');
      setCompareUser(null);
    } finally {
      setCompareLoading(false);
    }
  };

  // Linhas do comparador, agrupadas pelos grupos visíveis do perfil atual
  const linhasComparador = useMemo(() => {
    if (!comparePerms) return [];
    return gruposVisiveis
      .map(g => {
        const itens = g.itens
          .map(it => {
            const eu     = permissoesUsuario.has(it.id);
            const outro  = comparePerms.has(it.id);
            return { ...it, eu, outro, diferente: eu !== outro };
          })
          .filter(it => (compareSomenteDif ? it.diferente : true));
        return { ...g, itens };
      })
      .filter(g => g.itens.length > 0);
  }, [comparePerms, gruposVisiveis, permissoesUsuario, compareSomenteDif]);

  const totalDifsComparador = useMemo(() => {
    if (!comparePerms) return 0;
    let n = 0;
    universoEditavel.forEach(id => {
      if (permissoesUsuario.has(id) !== comparePerms.has(id)) n++;
    });
    return n;
  }, [comparePerms, permissoesUsuario, universoEditavel]);

  const puxarPermissao = (id) => {
    if (!comparePerms) return;
    const querLigar = comparePerms.has(id);
    const novo = new Set(permissoesUsuario);
    if (querLigar) {
      novo.add(id);
      const parent = CHILD_TO_PARENT[id];
      if (parent) novo.add(parent);
    } else {
      novo.delete(id);
      if (PERMISSION_DEPENDENCIES[id]) PERMISSION_DEPENDENCIES[id].forEach(c => novo.delete(c));
    }
    setPermissoesUsuario(normalizePermissions(novo));
  };

  const puxarTodasDiferencas = () => {
    if (!comparePerms) return;
    const novo = new Set();
    // dentro do universo editável, espelha o comparado
    universoEditavel.forEach(id => { if (comparePerms.has(id)) novo.add(id); });
    // preserva o que estiver fora do universo (não editável aqui)
    permissoesUsuario.forEach(id => { if (!universoEditavel.has(id)) novo.add(id); });
    setPermissoesUsuario(normalizePermissions(novo));
    toast.success('Diferenças alinhadas. Revise abaixo e clique em Salvar.');
  };

  const usuariosFiltrados = useMemo(() => {
    const t = safeLower(termoBusca, '');
    return usuarios.filter(u => {
      const matchNome   = safeLower(u.nome, '').includes(t) || safeLower(u.email, '').includes(t);
      const matchPerfil = filtroPerfil === 'todos' ? true : u.perfil === filtroPerfil;
      return matchNome && matchPerfil;
    });
  }, [usuarios, termoBusca, filtroPerfil]);

  const countEnabledInGroup = useCallback((itens) => {
    let n = 0;
    (itens || []).forEach(i => { if (permissoesUsuario.has(i.id)) n++; });
    return n;
  }, [permissoesUsuario]);

  // Lista de candidatos para os modais (exclui o próprio usuário selecionado)
  const candidatosModal = useCallback((busca) => {
    const t = safeLower(busca, '');
    return usuarios
      .filter(u => u.id !== usuarioSelecionado?.id)
      .filter(u =>
        !t ||
        safeLower(u.nome, '').includes(t) ||
        safeLower(u.email, '').includes(t) ||
        safeLower(u.perfil, '').includes(t)
      );
  }, [usuarios, usuarioSelecionado]);

  return (
    <div className="gp-container">
      <header className="gp-header">
        <div className="gp-header-title">
          <h1>Gestão de Acessos</h1>
          <p>Controle granular de visibilidade das abas e sub-menus.</p>
        </div>
        <div className="gp-header-actions">
          <button className="gp-btn-refresh" onClick={carregarUsuarios} title="Recarregar Lista">
            <FaSyncAlt className={loading ? 'spin' : ''} />
          </button>
          <button
            className={`gp-btn-save ${permissoesAlteradas ? 'pulse' : ''}`}
            disabled={!usuarioSelecionado || saving || !permissoesAlteradas}
            onClick={salvarPermissoes}
          >
            <FaSave /> {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </header>

      <div className="gp-layout">
        <aside className="gp-sidebar">
          <div className="gp-search-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Buscar colaborador..."
              value={termoBusca}
              onChange={e => setTermoBusca(e.target.value)}
            />
          </div>
          <div className="gp-filter-tabs">
            {['todos', 'admin_geral', 'admin', 'financeiro', 'rh', 'operacional'].map(tipo => (
              <button
                key={tipo}
                className={filtroPerfil === tipo ? 'active' : ''}
                onClick={() => { setFiltroPerfil(tipo); setUsuarioSelecionado(null); }}
              >
                {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
              </button>
            ))}
          </div>
          <div className="gp-user-list custom-scroll">
            {loading ? (
              [1, 2, 3, 4].map(i => <div key={i} className="gp-skeleton-card" />)
            ) : (
              usuariosFiltrados.map(user => (
                <div
                  key={user.id}
                  className={`gp-user-card ${usuarioSelecionado?.id === user.id ? 'active' : ''}`}
                  onClick={() => setUsuarioSelecionado(user)}
                >
                  <div className="gp-avatar">
                    {safeString(user.nome, 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="gp-user-info">
                    <strong>{user.nome}</strong>
                    <span className={`badge badge-${user.perfil}`}>{user.perfil}</span>
                  </div>
                  <div className="gp-chevron">›</div>
                </div>
              ))
            )}
            {usuariosFiltrados.length === 0 && !loading && (
              <div className="gp-empty-list">Nenhum usuário encontrado.</div>
            )}
          </div>
        </aside>

        <main className="gp-content custom-scroll">
          {usuarioSelecionado ? (
            <div className="gp-permissions-wrapper">
              <div className="gp-user-banner">
                <div className="banner-avatar">{safeString(usuarioSelecionado.nome, 'U').charAt(0)}</div>
                <div className="banner-id">
                  <h2>{usuarioSelecionado.nome}</h2>
                  <p>{usuarioSelecionado.email} • {usuarioSelecionado.cargo || 'Colaborador'}</p>
                  <span className={`badge badge-${usuarioSelecionado.perfil} banner-badge`}>
                    {usuarioSelecionado.perfil}
                  </span>
                </div>

                <div className="banner-actions">
                  <button className="gp-btn-mirror" onClick={abrirEspelhamento} disabled={saving} title="Copiar acessos de outro usuário">
                    <FaClone /> Espelhar acessos
                  </button>
                  <button className="gp-btn-compare" onClick={abrirComparador} disabled={saving} title="Comparar acessos lado a lado">
                    <FaBalanceScaleLeft /> Comparar
                  </button>
                  <button className="gp-btn-magic" onClick={aplicarPadraoPerfil} disabled={saving}>
                    <FaMagic /> Aplicar padrão
                  </button>
                  <div className="banner-stat">
                    <span>Liberados</span>
                    <strong>{permissoesUsuario.size}</strong>
                  </div>
                </div>
              </div>

              <div className="gp-toolbar">
                <div className="gp-perm-search">
                  <FaSearch />
                  <input
                    type="text"
                    placeholder="Filtrar permissões nesta tela..."
                    value={filtroPermissao}
                    onChange={e => setFiltroPermissao(e.target.value)}
                  />
                  {filtroPermissao && (
                    <button className="gp-perm-search-clear" onClick={() => setFiltroPermissao('')} title="Limpar">
                      <FaTimes />
                    </button>
                  )}
                </div>
                <div className="gp-toolbar-meta">
                  {gruposVisiveis.reduce((acc, g) => acc + g.itens.length, 0)} permissões disponíveis neste perfil
                </div>
              </div>

              <div className="gp-modules-grid">
                {gruposFiltrados.map((grupo) => (
                  <div key={grupo.nome} className="gp-module-block">
                    <div className="gp-module-head">
                      <h3 style={{ '--accent-color': grupo.cor }}>{grupo.nome}</h3>
                      <div className="gp-group-actions">
                        <button type="button" className="gp-btn-secondary" disabled={saving} onClick={() => marcarTudoGrupo(grupo.itens, true)}>
                          Marcar tudo
                        </button>
                        <button type="button" className="gp-btn-ghost-danger" disabled={saving} onClick={() => marcarTudoGrupo(grupo.itens, false)}>
                          Desmarcar
                        </button>
                        <span className="gp-group-count">
                          <strong>{countEnabledInGroup(grupo.itens)}</strong> / {grupo.itens.length}
                        </span>
                      </div>
                    </div>
                    <div className="gp-toggles-list">
                      {grupo.itens.map(item => {
                        const ativo = permissoesUsuario.has(item.id);
                        return (
                          <label
                            key={item.id}
                            className={`gp-toggle-item ${ativo ? 'is-on' : ''} ${saving ? 'disabled' : ''}`}
                            style={{ '--accent-color': grupo.cor }}
                          >
                            <div className="toggle-icon">{item.icon}</div>
                            <div className="toggle-info">
                              <span className="toggle-title">{item.titulo}</span>
                              <span className="toggle-desc">{item.desc}</span>
                            </div>
                            <div className="toggle-switch-wrapper">
                              <input
                                type="checkbox"
                                disabled={saving}
                                checked={ativo}
                                onChange={() => togglePermissao(item.id)}
                              />
                              <span className="slider round" />
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {gruposVisiveis.length === 0 && (
                  <div className="gp-empty-state">
                    <FaUserCog size={48} />
                    <h3>Configuração padrão</h3>
                    <p>Este perfil não possui módulos opcionais configuráveis.</p>
                  </div>
                )}

                {gruposVisiveis.length > 0 && gruposFiltrados.length === 0 && (
                  <div className="gp-empty-state">
                    <FaSearch size={40} />
                    <h3>Nada encontrado</h3>
                    <p>Nenhuma permissão corresponde a "{filtroPermissao}".</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="gp-welcome">
              <div className="welcome-icon-bg"><FaUserCog /></div>
              <h2>Selecione um colaborador</h2>
              <p>Escolha alguém na lista lateral para gerenciar os acessos.</p>
            </div>
          )}
        </main>
      </div>

      {/* ===================== BARRA DE ALTERAÇÕES (DIFF) ===================== */}
      {usuarioSelecionado && permissoesAlteradas && (
        <div className="gp-changeset">
          <div className="gp-changeset-summary">
            <span className="gp-changeset-pill gp-changeset-pill--add">
              <FaPlus /> {changeset.adicionadas.length} liberadas
            </span>
            <span className="gp-changeset-pill gp-changeset-pill--remove">
              <FaMinus /> {changeset.removidas.length} removidas
            </span>
            <div className="gp-changeset-chips custom-scroll">
              {changeset.adicionadas.map(id => (
                <span key={`a-${id}`} className="gp-chip gp-chip--add" title={tituloDe(id)}>
                  <FaPlus /> {tituloDe(id)}
                </span>
              ))}
              {changeset.removidas.map(id => (
                <span key={`r-${id}`} className="gp-chip gp-chip--remove" title={tituloDe(id)}>
                  <FaMinus /> {tituloDe(id)}
                </span>
              ))}
            </div>
          </div>
          <div className="gp-changeset-actions">
            <button className="gp-btn-ghost" onClick={reverterAlteracoes} disabled={saving}>
              <FaUndo /> Descartar
            </button>
            <button className={`gp-btn-save ${permissoesAlteradas ? 'pulse' : ''}`} onClick={salvarPermissoes} disabled={saving}>
              <FaSave /> {saving ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      )}

      {/* ===================== MODAL: ESPELHAR ACESSOS ===================== */}
      {mirrorOpen && (
        <div className="gp-modal-backdrop" onClick={() => setMirrorOpen(false)}>
          <div className="gp-modal gp-modal--mirror" onClick={e => e.stopPropagation()}>
            <div className="gp-modal-header">
              <div className="gp-modal-title">
                <FaClone />
                <div>
                  <h3>Espelhar acessos</h3>
                  <p>Copie integralmente os acessos de um colaborador para <strong>{usuarioSelecionado?.nome}</strong>.</p>
                </div>
              </div>
              <button className="gp-modal-close" onClick={() => setMirrorOpen(false)}><FaTimes /></button>
            </div>

            <div className="gp-modal-body">
              <div className="gp-mirror-flow">
                <div className="gp-mirror-node gp-mirror-node--from">
                  <span className="gp-mirror-node-label">De (origem)</span>
                  <strong>{mirrorSource ? mirrorSource.nome : 'Selecione abaixo'}</strong>
                </div>
                <FaArrowRight className="gp-mirror-arrow" />
                <div className="gp-mirror-node gp-mirror-node--to">
                  <span className="gp-mirror-node-label">Para (destino)</span>
                  <strong>{usuarioSelecionado?.nome}</strong>
                </div>
              </div>

              <div className="gp-modal-search">
                <FaSearch />
                <input
                  type="text"
                  placeholder="Buscar usuário de origem..."
                  value={mirrorBusca}
                  onChange={e => setMirrorBusca(e.target.value)}
                />
              </div>

              <div className="gp-pick-list custom-scroll">
                {candidatosModal(mirrorBusca).map(u => (
                  <button
                    key={u.id}
                    className={`gp-pick-item ${mirrorSource?.id === u.id ? 'active' : ''}`}
                    onClick={() => selecionarOrigemEspelho(u)}
                  >
                    <div className="gp-pick-avatar">{safeString(u.nome, 'U').charAt(0).toUpperCase()}</div>
                    <div className="gp-pick-info">
                      <strong>{u.nome}</strong>
                      <span>{u.email}</span>
                    </div>
                    <span className={`badge badge-${u.perfil}`}>{u.perfil}</span>
                    {mirrorSource?.id === u.id && <FaCheck className="gp-pick-check" />}
                  </button>
                ))}
                {candidatosModal(mirrorBusca).length === 0 && (
                  <div className="gp-empty-list">Nenhum usuário disponível.</div>
                )}
              </div>

              {mirrorLoading && <div className="gp-modal-loading"><FaSyncAlt className="spin" /> Carregando acessos...</div>}

              {previaEspelho && !mirrorLoading && (
                <div className="gp-mirror-preview">
                  <div className="gp-preview-head">
                    <FaInfoCircle />
                    Prévia do que será aplicado a <strong>{usuarioSelecionado?.nome}</strong>
                  </div>
                  <div className="gp-preview-cols">
                    <div className="gp-preview-col gp-preview-col--add">
                      <span className="gp-preview-col-title"><FaPlus /> Vai liberar ({previaEspelho.adicionadas.length})</span>
                      <div className="gp-preview-items custom-scroll">
                        {previaEspelho.adicionadas.length === 0
                          ? <em>Nenhuma novidade.</em>
                          : previaEspelho.adicionadas.map(id => <span key={id} className="gp-chip gp-chip--add">{tituloDe(id)}</span>)}
                      </div>
                    </div>
                    <div className="gp-preview-col gp-preview-col--remove">
                      <span className="gp-preview-col-title"><FaMinus /> Vai remover ({previaEspelho.removidas.length})</span>
                      <div className="gp-preview-items custom-scroll">
                        {previaEspelho.removidas.length === 0
                          ? <em>Nada será removido.</em>
                          : previaEspelho.removidas.map(id => <span key={id} className="gp-chip gp-chip--remove">{tituloDe(id)}</span>)}
                      </div>
                    </div>
                  </div>
                  {previaEspelho.foraDeEscopo > 0 && (
                    <div className="gp-preview-warn">
                      <FaInfoCircle /> {previaEspelho.foraDeEscopo} acesso(s) da origem estão fora do escopo do perfil
                      <strong> {usuarioSelecionado?.perfil}</strong> e serão ignorados.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="gp-modal-footer">
              <button className="gp-btn-ghost" onClick={() => setMirrorOpen(false)}>Cancelar</button>
              <button
                className="gp-btn-mirror"
                disabled={!previaEspelho || mirrorLoading}
                onClick={confirmarEspelhamento}
              >
                <FaClone /> Espelhar acessos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===================== MODAL: COMPARADOR ===================== */}
      {compareOpen && (
        <div className="gp-modal-backdrop" onClick={() => setCompareOpen(false)}>
          <div className="gp-modal gp-modal--compare" onClick={e => e.stopPropagation()}>
            <div className="gp-modal-header">
              <div className="gp-modal-title">
                <FaBalanceScaleLeft />
                <div>
                  <h3>Comparar acessos</h3>
                  <p>Veja lado a lado e puxe permissões individuais de outro colaborador.</p>
                </div>
              </div>
              <button className="gp-modal-close" onClick={() => setCompareOpen(false)}><FaTimes /></button>
            </div>

            <div className="gp-modal-body">
              {!compareUser && (
                <>
                  <div className="gp-modal-search">
                    <FaSearch />
                    <input
                      type="text"
                      placeholder="Buscar usuário para comparar..."
                      value={compareBusca}
                      onChange={e => setCompareBusca(e.target.value)}
                    />
                  </div>
                  <div className="gp-pick-list custom-scroll">
                    {candidatosModal(compareBusca).map(u => (
                      <button key={u.id} className="gp-pick-item" onClick={() => selecionarComparado(u)}>
                        <div className="gp-pick-avatar">{safeString(u.nome, 'U').charAt(0).toUpperCase()}</div>
                        <div className="gp-pick-info">
                          <strong>{u.nome}</strong>
                          <span>{u.email}</span>
                        </div>
                        <span className={`badge badge-${u.perfil}`}>{u.perfil}</span>
                      </button>
                    ))}
                    {candidatosModal(compareBusca).length === 0 && (
                      <div className="gp-empty-list">Nenhum usuário disponível.</div>
                    )}
                  </div>
                </>
              )}

              {compareLoading && <div className="gp-modal-loading"><FaSyncAlt className="spin" /> Carregando comparação...</div>}

              {compareUser && comparePerms && !compareLoading && (
                <div className="gp-compare">
                  <div className="gp-compare-bar">
                    <div className="gp-compare-who">
                      <span className="gp-compare-who-a"><FaUsers /> {usuarioSelecionado?.nome}</span>
                      <FaEquals className="gp-compare-vs" />
                      <span className="gp-compare-who-b"><FaUsers /> {compareUser.nome}</span>
                      <button className="gp-compare-change" onClick={() => { setCompareUser(null); setComparePerms(null); }}>
                        trocar
                      </button>
                    </div>
                    <div className="gp-compare-tools">
                      <label className="gp-compare-onlydiff">
                        <input
                          type="checkbox"
                          checked={compareSomenteDif}
                          onChange={e => setCompareSomenteDif(e.target.checked)}
                        />
                        Só diferenças
                      </label>
                      <button
                        className="gp-btn-mirror gp-btn-sm"
                        disabled={totalDifsComparador === 0}
                        onClick={puxarTodasDiferencas}
                        title="Alinhar todas as diferenças com o usuário comparado"
                      >
                        <FaDownload /> Puxar todas ({totalDifsComparador})
                      </button>
                    </div>
                  </div>

                  <div className="gp-compare-list custom-scroll">
                    {linhasComparador.length === 0 && (
                      <div className="gp-empty-list">
                        {compareSomenteDif ? 'Sem diferenças neste perfil. Tudo igual! 🎉' : 'Nada a exibir.'}
                      </div>
                    )}
                    {linhasComparador.map(grupo => (
                      <div key={grupo.nome} className="gp-compare-group">
                        <div className="gp-compare-group-title" style={{ '--accent-color': grupo.cor }}>
                          {grupo.nome}
                        </div>
                        {grupo.itens.map(it => (
                          <div key={it.id} className={`gp-compare-row ${it.diferente ? 'is-diff' : ''}`}>
                            <div className="gp-compare-name">
                              <span className="gp-compare-ic">{it.icon}</span>
                              {it.titulo}
                            </div>
                            <div className={`gp-compare-dot ${it.eu ? 'yes' : 'no'}`} title={usuarioSelecionado?.nome}>
                              {it.eu ? <FaCheck /> : <FaTimes />}
                            </div>
                            <div className={`gp-compare-dot ${it.outro ? 'yes' : 'no'}`} title={compareUser.nome}>
                              {it.outro ? <FaCheck /> : <FaTimes />}
                            </div>
                            <button
                              className="gp-pull-btn"
                              disabled={!it.diferente}
                              onClick={() => puxarPermissao(it.id)}
                              title={it.outro ? 'Liberar para o usuário atual' : 'Remover do usuário atual'}
                            >
                              {it.diferente ? (it.outro ? <><FaArrowRight /> Puxar</> : <><FaMinus /> Tirar</>) : 'OK'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="gp-modal-footer">
              <button className="gp-btn-ghost" onClick={() => setCompareOpen(false)}>Fechar</button>
              {permissoesAlteradas && (
                <button className="gp-btn-save pulse" onClick={() => { salvarPermissoes(); setCompareOpen(false); }} disabled={saving}>
                  <FaSave /> Salvar alterações
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}